// A dropdown custom field's popover body on the Opportunities list. The same list surface as the
// Salesperson editor: the options in definition order with the current one tinted, a save on selection,
// and "None" to clear (offered only when there is a value to clear). The options come from the field
// definition the page already loaded, so opening the editor runs no query.
import React, {useRef} from 'react';
import {Text} from '@forecast-it/design-system';
import {Divider, List, Option} from '../editorOptionList';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

export const DropdownFieldEditor = ({node, def, current, onCommit, close, labels}: CustomFieldEditorProps): JSX.Element => {
	const hasValue = current != null && current !== '';
	// The popover stays mounted through its exit animation, so a second click could otherwise save again.
	const saving = useRef(false);
	const select = (value: string | null) => {
		if (saving.current) return;
		saving.current = true;
		close();
		// Re-picking the current option would commit a no-op mutation and refetch the whole list.
		if (value === current) return;
		saveCustomFieldValue({node, def, value, onCommit});
	};
	return (
		<List data-cy="cf-dropdown-editor">
			{def.options.map(option => (
				<Option
					key={option.id}
					type="button"
					selected={option.key === current}
					aria-current={option.key === current ? 'true' : undefined}
					data-cy={`cf-option-${option.key}`}
					onClick={() => select(option.key)}
				>
					<Text>{option.displayName}</Text>
				</Option>
			))}
			{hasValue && (
				<>
					<Divider />
					<Option type="button" selected={false} data-cy="cf-option-none" onClick={() => select(null)}>
						<Text>{labels.none}</Text>
					</Option>
				</>
			)}
		</List>
	);
};

export default DropdownFieldEditor;
